import React, { useState, useEffect, useRef, useMemo } from "react";
import { Popover, Select, MenuItem, FormControl } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import "../analytics/analytics.scss";
import "../basic_analytics/BasicAnalytics.scss";
import PatientStats from "../../components/analytics_new/PatientStats";
import CtgSessions from "../../components/analytics_new/CtgSessions";
import MonitoringStats from "../../components/analytics_new/MonitoringStats";
import Cookie from "js-cookie";
import urls from "../../utils/url";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import Loading from "../../components/common/loading";
import { useResetOnBack } from "../../hooks/useResetOnBack";
import AnalyticsStatCards from "../../components/analytics_new/AnalyticsStatCards";
import StatusDonutCard from "../../components/analytics_new/StatusDonutCard";
import AlertOverview from "../../components/analytics_new/AlertOverview";
import UniquePatientMonitored from "../../components/analytics_new/UniquePatientMonitored";

const CustomDay = ({ startDate, endDate, day, ...props }) => {
  const isStart = startDate && day.isSame(startDate, "day");
  const isEnd = endDate && day.isSame(endDate, "day");
  const isInRange =
    startDate &&
    endDate &&
    day.isAfter(startDate, "day") &&
    day.isBefore(endDate, "day");

  return (
    <PickersDay
      {...props}
      day={day}
      sx={{
        ...(isInRange && {
          backgroundColor: "#F6F2FF !important",
          borderRadius: "0 !important",
          color: "rgba(108, 23, 217, 1) !important",
        }),
        ...((isStart || isEnd) && {
          backgroundColor: "rgba(232, 212, 255, 1) !important",
          color: "#1a1a1a  !important",
          borderRadius: "50% !important",
        }),
      }}
    />
  );
};

const DATE_FILTER_LABELS = {
  all: "Till Date",
  month: "Current Month",
  day: "Today",
  yesterday: "Yesterday",
  week: "Last 7 days",
  last15days: "Last 15 Days",
  last30days: "Last 30 Days",
};

const RECORDING_BUCKETS = [
  { key: "recording_1", label: "1 recording" },
  { key: "recording_2", label: "2 recordings" },
  { key: "recording_3_4", label: "3-4 recordings" },
  { key: "recording_5_plus", label: "5+ recordings" },
];

function UltraAdminAnalytics() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [ultraAdminZones, setUltraAdminZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [patientChartZones, setPatientChartZones] = useState([]);
  const [monitoringChartZones, setMonitoringChartZones] = useState([]);

  const zoneOptions = useMemo(
    () =>
      ultraAdminZones.map((z) => ({
        id: z.zone_place_id,
        name: z.zone_place_name,
      })),
    [ultraAdminZones],
  );

  const districtOptions = useMemo(() => {
    const relevantZones =
      selectedZone === "all"
        ? ultraAdminZones
        : ultraAdminZones.filter((z) => z.zone_place_id === selectedZone);
    const districts = new Set();
    relevantZones.forEach((z) => {
      (z.facilities || []).forEach((f) => {
        if (f.district) districts.add(f.district);
      });
    });
    return Array.from(districts);
  }, [ultraAdminZones, selectedZone]);

  const facilityOptionsFromZone = useMemo(() => {
    const relevantZones =
      selectedZone === "all"
        ? ultraAdminZones
        : ultraAdminZones.filter((z) => z.zone_place_id === selectedZone);
    const allFacilities = relevantZones.flatMap((z) => z.facilities || []);
    const filtered =
      selectedDistrict === "all"
        ? allFacilities
        : allFacilities.filter((f) => f.district === selectedDistrict);
    return filtered.map((f) => ({ id: f.place_id, name: f.place_name }));
  }, [ultraAdminZones, selectedZone, selectedDistrict]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isSelectingStartDate, setIsSelectingStartDate] = useState(true);

  // "all" — no date params sent — is the default until the user actively
  // picks a date filter, rather than implicitly scoping to the current month.
  const [globalDateFilter, setGlobalDateFilter] = useState("all");

  const [selectedFacility, setSelectedFacility] = useState("all");

  const isFiltered =
    selectedZone !== "all" ||
    selectedDistrict !== "all" ||
    selectedFacility !== "all" ||
    globalDateFilter !== "all" ||
    !!startDate ||
    !!endDate;

  const resetAllFilters = () => {
    setSelectedZone("all");
    setSelectedDistrict("all");
    setSelectedFacility("all");
    setGlobalDateFilter("all");
    setStartDate(null);
    setEndDate(null);
    document.activeElement?.blur();
  };

  useResetOnBack(isFiltered, resetAllFilters);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = sessionStorage.getItem("sidebarCollapsed");
    return stored ? JSON.parse(stored) : false;
  });

  const [patientData, setPatientData] = useState([]);
  const [monitoringData, setMonitoringData] = useState([]);
  const [ctgSessionsData, setCtgSessionsData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [isPatientLoading, setIsPatientLoading] = useState(true);
  const [isMonitoringLoading, setIsMonitoringLoading] = useState(true);
  // Set when a fetch that feeds Patient Stats/CTG Sessions or Monitoring
  // Stats genuinely fails (network error, non-OK response) — not for a slow
  // response (that's isLoading) or a successful-but-empty result (that's the
  // existing NoData empty state). Reset to false at the start of each fetch
  // so a later successful retry/facility-switch clears a previous error.
  const [patientError, setPatientError] = useState(false);
  const [monitoringError, setMonitoringError] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);

  const requestCacheRef = useRef(new Map());
  const facilityOverviewRequestIdRef = useRef(0);
  const availableFacilitiesRequestIdRef = useRef(0);
  const patientRequestIdRef = useRef(0);
  const monitoringRequestIdRef = useRef(0);
  const deviceOverviewRequestIdRef = useRef(0);

  const token = Cookie.get("access_token");

  const hospitalDetails = JSON.parse(Cookie.get("hospital_detail") || "{}");

  const loggedInPlaceId = hospitalDetails?.hospitalID;
  // console.log("PLACE ID:", loggedInPlaceId);

  useEffect(() => {
    const handleSidebarStateChange = (event) => {
      setIsSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener("sidebarStateChange", handleSidebarStateChange);

    return () => {
      window.removeEventListener(
        "sidebarStateChange",
        handleSidebarStateChange,
      );
    };
  }, []);

  const handleCalendarOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
    setIsSelectingStartDate(true);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setGlobalDateFilter("all");
    setIsSelectingStartDate(true);
  };

  const handleZoneChange = (value) => {
    setSelectedZone(value);
    setSelectedDistrict("all");
    setSelectedFacility("all");
  };

  // Picking a district directly (while Zone is still "all") should back-fill
  // Zone to the district's owning zone, matching handleFacilitySelect's
  // equivalent zone+district back-fill below.
  const handleDistrictChange = (value) => {
    if (value !== "all") {
      const ownerZone = ultraAdminZones.find((z) =>
        (z.facilities || []).some((f) => f.district === value),
      );
      if (ownerZone) setSelectedZone(ownerZone.zone_place_id);
    }
    setSelectedDistrict(value);
    setSelectedFacility("all");
  };

  // Picking a facility directly (while Zone/District are still "all") should
  // back-fill those dropdowns to the facility's actual zone/district, so the
  // hierarchy path stays visually consistent no matter which dropdown was
  // used to get there.
  const handleFacilitySelect = (facilityId) => {
    if (facilityId === "all") {
      setSelectedFacility("all");
      return;
    }
    const ownerZone = ultraAdminZones.find((z) =>
      (z.facilities || []).some((f) => f.place_id === facilityId),
    );
    const facility = ownerZone?.facilities.find(
      (f) => f.place_id === facilityId,
    );
    if (ownerZone) setSelectedZone(ownerZone.zone_place_id);
    if (facility?.district) setSelectedDistrict(facility.district);
    setSelectedFacility(facilityId);
  };

  const getCurrentHierarchyLevel = () => {
    if (selectedFacility !== "all") return "device";
    if (selectedDistrict !== "all") return "facility";
    if (selectedZone !== "all") return "district";
    return "zone";
  };

  const resolveBarIdToFilterValue = (barData, hierarchyLevel) => {
    if (!barData?.id) return null;
    const barName = barData.id;

    switch (hierarchyLevel) {
      case "zone": {
        const zone = zoneOptions.find((z) => z.name === barName);
        return zone?.id ?? null;
      }
      case "district":
        return districtOptions.includes(barName) ? barName : null;
      case "facility":
        return barData.placeId ?? null;
      default:
        return null;
    }
  };

  const handleBarClick = (barData) => {
    try {
      if (!barData || typeof barData.id !== "string" || !barData.id.trim()) {
        console.error("Bar click: invalid bar data structure", barData);
        return;
      }

      const level = getCurrentHierarchyLevel();
      if (level === "device") return; // terminal — devices, no further drill

      const filterValue = resolveBarIdToFilterValue(barData, level);
      if (!filterValue) {
        console.warn(
          `Bar click: could not resolve "${barData.id}" at level "${level}"`,
        );
        return;
      }

      if (level === "zone") {
        handleZoneChange(filterValue);
      } else if (level === "district") {
        handleDistrictChange(filterValue);
      } else if (level === "facility") {
        setSelectedFacility(filterValue);
      }
    } catch (err) {
      console.error(
        "Bar click: unexpected error while processing drill-down",
        err,
      );
    }
  };

  const handleDateSelect = (date) => {
    if (isSelectingStartDate) {
      setStartDate(date);
      setEndDate(null);
      setGlobalDateFilter("custom");
      setIsSelectingStartDate(false);
    } else {
      setEndDate(date);
      setGlobalDateFilter("custom");
      if (
        startDate &&
        date &&
        (date.isAfter(startDate) || date.isSame(startDate))
      ) {
        handleCalendarClose();
      } else {
        setEndDate(startDate);
        setStartDate(date);
        handleCalendarClose();
      }
    }
  };

  const getDateRange = (filterType) => {
    const today = dayjs();
    if (filterType === "all") return { start: null, end: null };
    if (filterType === "day") return { start: today, end: today };
    if (filterType === "yesterday") {
      const yesterday = today.subtract(1, "day");
      return { start: yesterday, end: yesterday };
    }
    if (filterType === "week")
      return { start: today.subtract(6, "day"), end: today };
    if (filterType === "last15days")
      return { start: today.subtract(14, "day"), end: today };
    if (filterType === "last30days")
      return { start: today.subtract(29, "day"), end: today };

    if (filterType === "month")
      return { start: today.startOf("month"), end: today };

    if (filterType === "custom") return { start: startDate, end: endDate };
    return { start: today, end: today };
  };

  const getApiParams = (filterType) => {
    const { start, end } = getDateRange(filterType);
    return {
      // No place_ids when "all" — API will return data for all places
      ...(selectedFacility && selectedFacility !== "all"
        ? { place_ids: selectedFacility }
        : {}),
      ...(start &&
        end && {
          from_time: start.format("YYYY-MM-DD"),
          to_time: end.add(1, "day").format("YYYY-MM-DD"),
        }),
    };
  };

  const getHeaderParams = () => ({
    ...(selectedFacility && selectedFacility !== "all"
      ? { place_ids: selectedFacility }
      : {}),
    ...(startDate &&
      endDate && {
        from_time: startDate.format("YYYY-MM-DD"),
        to_time: endDate.add(1, "day").format("YYYY-MM-DD"),
      }),
  });

  const getAvailableFacilitiesParams = () => ({
    ...(startDate &&
      endDate && {
        from_time: startDate.format("YYYY-MM-DD"),
        to_time: endDate.add(1, "day").format("YYYY-MM-DD"),
      }),
  });

  const buildQueryString = (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        query.append(key, String(value));
      }
    });
    return query.toString();
  };

  const fetchAnalytics = async (endpoint, params = {}, version = "v1") => {
    const queryString = buildQueryString(params);
    const basePath = version === "v2" ? "api/analytics/v2/" : "api/analytics/";
    const url = `${urls.BETA_URL}${basePath}${endpoint}/?${queryString ? queryString : ""}`;
    const cacheKey = `${version}:${endpoint}?${queryString}`;

    if (requestCacheRef.current.has(cacheKey)) {
      return requestCacheRef.current.get(cacheKey);
    }

    const requestPromise = fetch(url, {
      headers: { Authorization: `Bearer ${Cookie.get("access_token")}` },
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Request failed (${res.status}) for ${endpoint}`);
      }
      return res.json();
    });

    requestCacheRef.current.set(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      requestCacheRef.current.delete(cacheKey);
    }
  };

  // Device-level drill-down (a specific facility selected): one shared
  // device-overview fetch feeds Patient Stats, Monitoring Stats, and CTG
  // Sessions, instead of three separate calls. avg_monitoring_hours_per_device
  // doesn't exist per-device (only in the response's root `summary`, which is
  // a facility-wide aggregate) — a single device's own average is just its
  // own hours_monitored (dividing by 1 device is mathematically valid).
  const fetchDeviceOverview = async () => {
    // Its own dedicated ref — one per independent fetch, like
    // facilityOverviewRequestIdRef/availableFacilitiesRequestIdRef below —
    // not the patient/monitoring refs those two zone-level fetches own.
    // Sharing/syncing those caused a real race: rapidly switching
    // selectedFacility back to "all" could bump monitoringRequestIdRef via
    // fetchUltraAdminMonitoringChart while this call was still in flight,
    // and this call had no way to notice, so it could still overwrite
    // monitoringData with stale device-level data (or clear its loading
    // flag early) after the user had already moved to the zone view.
    const requestId = ++deviceOverviewRequestIdRef.current;
    setIsPatientLoading(true);
    setIsMonitoringLoading(true);
    setPatientError(false);
    setMonitoringError(false);
    try {
      const params = getApiParams(globalDateFilter);
      const data = await fetchAnalytics("device-overview", params);
      if (requestId !== deviceOverviewRequestIdRef.current) return;

      // Backend sometimes returns a worker_id: null "Unassigned / Other"
      // row (unmapped device) — drop it until that's fixed server-side.
      const results = (data.results || []).filter(
        (item) => item.worker_id != null,
      );

      setPatientData(
        results.map((item) => ({
          id: item.first_name,
          patients: item.mothers_added,
        })),
      );

      setMonitoringData(
        results.map((item) => ({
          id: item.first_name,
          hours: item.hours_monitored,
          avgPerDevice: item.hours_monitored ?? 0,
          avgPerPregnantWoman:
            item.avg_monitoring_hours_per_pregnant_woman ?? 0,
        })),
      );

      setCtgSessionsData(
        results.map((item) => ({
          id: item.first_name,
          sessions: item.ctg_sessions || 0,
        })),
      );
    } catch (err) {
      console.error(err);
      if (requestId === deviceOverviewRequestIdRef.current) {
        setPatientData([]);
        setMonitoringData([]);
        setCtgSessionsData([]);
        setPatientError(true);
        setMonitoringError(true);
      }
    } finally {
      if (requestId === deviceOverviewRequestIdRef.current) {
        setIsPatientLoading(false);
        setIsMonitoringLoading(false);
      }
    }
  };

  const fetchUltraAdminOverview = async () => {
    const requestId = ++facilityOverviewRequestIdRef.current;
    setIsSummaryLoading(true);
    setIsPatientLoading(true);
    setIsMonitoringLoading(true);
    try {
      const { start, end } = getDateRange(globalDateFilter);
      const params =
        start && end
          ? {
              from_time: start.format("YYYY-MM-DD"),
              to_time: end.add(1, "day").format("YYYY-MM-DD"),
            }
          : {};
      const data = await fetchAnalytics("facility-overview", params, "v2");
      if (requestId !== facilityOverviewRequestIdRef.current) return;
      setSummaryData(data.summary);
      const filteredZones = (data.zones || []).filter(
        (z) => z.zone_place_id !== data.parent_place_id,
      );
      setUltraAdminZones(filteredZones);
      setPatientChartZones(filteredZones);
      setMonitoringChartZones(filteredZones);
    } catch (err) {
      console.error(err);
    } finally {
      if (requestId === facilityOverviewRequestIdRef.current) {
        setIsSummaryLoading(false);
        setIsPatientLoading(false);
        setIsMonitoringLoading(false);
      }
    }
  };

  const fetchUltraAdminPatientChart = async (filterType) => {
    const requestId = ++patientRequestIdRef.current;
    setIsPatientLoading(true);
    setPatientError(false);
    try {
      const { start, end } = getDateRange(filterType);
      const params =
        start && end
          ? {
              from_time: start.format("YYYY-MM-DD"),
              to_time: end.add(1, "day").format("YYYY-MM-DD"),
            }
          : {};
      const data = await fetchAnalytics("facility-overview", params, "v2");
      if (requestId !== patientRequestIdRef.current) return;
      setPatientChartZones(
        (data.zones || []).filter(
          (z) => z.zone_place_id !== data.parent_place_id,
        ),
      );
    } catch (err) {
      console.error(err);
      if (requestId === patientRequestIdRef.current) {
        setPatientChartZones([]);
        setPatientError(true);
      }
    } finally {
      if (requestId === patientRequestIdRef.current) setIsPatientLoading(false);
    }
  };

  const fetchUltraAdminMonitoringChart = async (filterType) => {
    const requestId = ++monitoringRequestIdRef.current;
    setIsMonitoringLoading(true);
    setMonitoringError(false);
    try {
      const { start, end } = getDateRange(filterType);
      const params =
        start && end
          ? {
              from_time: start.format("YYYY-MM-DD"),
              to_time: end.add(1, "day").format("YYYY-MM-DD"),
            }
          : {};
      const data = await fetchAnalytics("facility-overview", params, "v2");
      if (requestId !== monitoringRequestIdRef.current) return;
      setMonitoringChartZones(
        (data.zones || []).filter(
          (z) => z.zone_place_id !== data.parent_place_id,
        ),
      );
    } catch (err) {
      console.error(err);
      if (requestId === monitoringRequestIdRef.current) {
        setMonitoringChartZones([]);
        setMonitoringError(true);
      }
    } finally {
      if (requestId === monitoringRequestIdRef.current)
        setIsMonitoringLoading(false);
    }
  };

  const ultraAdminChartFacilities = useMemo(() => {
    const relevantZones =
      selectedZone === "all"
        ? ultraAdminZones
        : ultraAdminZones.filter((z) => z.zone_place_id === selectedZone);
    const allFacilities = relevantZones.flatMap((z) => z.facilities || []);
    return selectedDistrict === "all"
      ? allFacilities
      : allFacilities.filter((f) => f.district === selectedDistrict);
  }, [ultraAdminZones, selectedZone, selectedDistrict]);

  const selectedZoneData = useMemo(
    () => ultraAdminZones.find((z) => z.zone_place_id === selectedZone),
    [ultraAdminZones, selectedZone],
  );

  const selectedFacilityData = useMemo(() => {
    if (selectedFacility === "all") return null;
    const allFacilities = ultraAdminZones.flatMap((z) => z.facilities || []);
    return allFacilities.find((f) => f.place_id === selectedFacility) || null;
  }, [ultraAdminZones, selectedFacility]);

  // A district counts as "active" if any facility within it (in the
  // currently selected zone) is active — the API has no district_status
  // field of its own, so this is derived from the facility list.
  const districtStatusCounts = useMemo(() => {
    const districtActiveMap = {};
    ultraAdminChartFacilities.forEach((f) => {
      const dist = f.district || "Unknown";
      districtActiveMap[dist] = districtActiveMap[dist] || !!f.is_active;
    });
    const values = Object.values(districtActiveMap);
    return {
      active: values.filter(Boolean).length,
      inactive: values.filter((v) => !v).length,
    };
  }, [ultraAdminChartFacilities]);

  const facilityStatusCounts = useMemo(
    () => ({
      active: ultraAdminChartFacilities.filter((f) => f.is_active).length,
      inactive: ultraAdminChartFacilities.filter((f) => !f.is_active).length,
    }),
    [ultraAdminChartFacilities],
  );

  // Sum of each facility's own device_status — used when scoped to a single
  // district, since the API's device_status aggregate only exists at the
  // zone level (totals) and the facility level, not per-district.
  const aggregatedDeviceStatus = useMemo(
    () =>
      ultraAdminChartFacilities.reduce(
        (acc, f) => ({
          active: acc.active + (f.device_status?.active || 0),
          disconnected: acc.disconnected + (f.device_status?.disconnected || 0),
        }),
        { active: 0, disconnected: 0 },
      ),
    [ultraAdminChartFacilities],
  );

  const aggregatedTraceCategory = useMemo(
    () =>
      ultraAdminChartFacilities.reduce(
        (acc, f) => ({
          normal: acc.normal + (f.trace_category?.normal || 0),
          suspicious: acc.suspicious + (f.trace_category?.suspicious || 0),
          pathological:
            acc.pathological + (f.trace_category?.pathological || 0),
        }),
        { normal: 0, suspicious: 0, pathological: 0 },
      ),
    [ultraAdminChartFacilities],
  );

  // No per-district API aggregate for this, so sum counts + total_unique_patients
  // across the district's facilities and recompute percentages from those sums —
  // can't average the facilities' own percentages, they're relative to each
  // facility's own patient count.
  const aggregatedPatientMonitoringFrequency = useMemo(() => {
    const counts = {};
    RECORDING_BUCKETS.forEach(({ key }) => {
      counts[key] = 0;
    });
    let totalUniquePatients = 0;

    ultraAdminChartFacilities.forEach((f) => {
      const freq = f.patient_monitoring_frequency || {};
      RECORDING_BUCKETS.forEach(({ key }) => {
        counts[key] += freq[key]?.count || 0;
      });
      totalUniquePatients += freq.total_unique_patients || 0;
    });

    const result = {};
    RECORDING_BUCKETS.forEach(({ key }) => {
      result[key] = {
        count: counts[key],
        percentage:
          totalUniquePatients > 0
            ? (counts[key] / totalUniquePatients) * 100
            : 0,
      };
    });
    return result;
  }, [ultraAdminChartFacilities]);

  const filteredSummary = useMemo(() => {
    if (selectedZone === "all" && selectedFacility === "all") {
      return summaryData;
    }

    let facilities = ultraAdminChartFacilities;

    if (selectedFacility !== "all") {
      const allFacilities = ultraAdminZones.flatMap((z) => z.facilities || []);
      const fac = allFacilities.find((f) => f.place_id === selectedFacility);
      facilities = fac ? [fac] : [];
    }

    return {
      total_devices: facilities.reduce(
        (sum, f) => sum + (f.total_devices || 0),
        0,
      ),
      total_hospitals: facilities.length,
      total_mothers_added: facilities.reduce(
        (sum, f) => sum + (f.mothers_added || 0),
        0,
      ),
      total_alerts_generated: facilities.reduce(
        (sum, f) => sum + (f.alerts_generated || 0),
        0,
      ),
      total_hours_monitored: parseFloat(
        facilities
          .reduce((sum, f) => sum + (f.hours_monitored || 0), 0)
          .toFixed(2),
      ),
      total_ctg_sessions: facilities.reduce(
        (sum, f) => sum + (f.ctg_sessions || 0),
        0,
      ),
      total_unique_patients_monitored: facilities.reduce(
        (sum, f) => sum + (f.unique_patients_monitored || 0),
        0,
      ),
    };
  }, [
    ultraAdminChartFacilities,
    selectedFacility,
    selectedZone,
    ultraAdminZones,
    summaryData,
  ]);

  // Patient chart only
  useEffect(() => {
    if (selectedFacility !== "all") return;
    if (selectedZone === "all") {
      setPatientData(
        patientChartZones.map((zone) => ({
          id: zone.zone_place_name,
          patients: (zone.facilities || []).reduce(
            (sum, f) => sum + (f.mothers_added || 0),
            0,
          ),
        })),
      );
    } else if (selectedDistrict === "all") {
      const zoneData = patientChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const districtMap = {};
      (zoneData?.facilities || []).forEach((f) => {
        const dist = f.district || "Unknown";
        if (!districtMap[dist]) districtMap[dist] = 0;
        districtMap[dist] += f.mothers_added || 0;
      });
      setPatientData(
        Object.entries(districtMap).map(([dist, patients]) => ({
          id: dist,
          patients,
        })),
      );
    } else {
      const zoneData = patientChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const facilities = (zoneData?.facilities || []).filter(
        (f) => f.district === selectedDistrict,
      );
      setPatientData(
        facilities.map((f) => ({
          id: f.place_name,
          placeId: f.place_id,
          patients: f.mothers_added,
        })),
      );
    }
    setIsPatientLoading(false);
  }, [patientChartZones, selectedFacility, selectedZone, selectedDistrict]);

  // CTG sessions chart — same categories/hierarchy as Patient Stats, sourced
  // from the same zones data (no separate fetch needed).
  useEffect(() => {
    // Device-level drill-down: fetchDeviceOverview already set real
    // ctgSessionsData from device-overview's own ctg_sessions field.
    if (selectedFacility !== "all") return;
    if (selectedZone === "all") {
      setCtgSessionsData(
        patientChartZones.map((zone) => ({
          id: zone.zone_place_name,
          sessions: (zone.facilities || []).reduce(
            (sum, f) => sum + (f.ctg_sessions || 0),
            0,
          ),
        })),
      );
    } else if (selectedDistrict === "all") {
      const zoneData = patientChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const districtMap = {};
      (zoneData?.facilities || []).forEach((f) => {
        const dist = f.district || "Unknown";
        if (!districtMap[dist]) districtMap[dist] = 0;
        districtMap[dist] += f.ctg_sessions || 0;
      });
      setCtgSessionsData(
        Object.entries(districtMap).map(([dist, sessions]) => ({
          id: dist,
          sessions,
        })),
      );
    } else {
      const zoneData = patientChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const facilities = (zoneData?.facilities || []).filter(
        (f) => f.district === selectedDistrict,
      );
      setCtgSessionsData(
        facilities.map((f) => ({
          id: f.place_name,
          placeId: f.place_id,
          sessions: f.ctg_sessions || 0,
        })),
      );
    }
  }, [patientChartZones, selectedFacility, selectedZone, selectedDistrict]);

  // Monitoring chart only
  useEffect(() => {
    if (selectedFacility !== "all") return;
    if (selectedZone === "all") {
      setMonitoringData(
        monitoringChartZones.map((zone) => ({
          id: zone.zone_place_name,
          hours: parseFloat(
            (zone.facilities || [])
              .reduce((sum, f) => sum + (f.hours_monitored || 0), 0)
              .toFixed(2),
          ),
          avgPerDevice: zone.totals?.avg_monitoring_hours_per_device ?? 0,
          avgPerPregnantWoman:
            zone.totals?.avg_monitoring_hours_per_pregnant_woman ?? 0,
        })),
      );
    } else if (selectedDistrict === "all") {
      const zoneData = monitoringChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const districtMap = {};
      (zoneData?.facilities || []).forEach((f) => {
        const dist = f.district || "Unknown";
        if (!districtMap[dist]) {
          districtMap[dist] = { hours: 0, deviceUnits: 0, mothers: 0 };
        }
        districtMap[dist].hours += f.hours_monitored || 0;
        // Derive the device count implied by the facility's own avg field
        // instead of reading any devices/total_devices field directly — this
        // stays correct regardless of how the backend defines "per device".
        if (f.avg_monitoring_hours_per_device > 0) {
          districtMap[dist].deviceUnits +=
            (f.hours_monitored || 0) / f.avg_monitoring_hours_per_device;
        }
        districtMap[dist].mothers += f.mothers_added || 0;
      });
      setMonitoringData(
        Object.entries(districtMap).map(([dist, totals]) => ({
          id: dist,
          hours: parseFloat(totals.hours.toFixed(2)),
          avgPerDevice:
            totals.deviceUnits > 0
              ? parseFloat((totals.hours / totals.deviceUnits).toFixed(2))
              : 0,
          avgPerPregnantWoman:
            totals.mothers > 0
              ? parseFloat((totals.hours / totals.mothers).toFixed(2))
              : 0,
        })),
      );
    } else {
      const zoneData = monitoringChartZones.find(
        (z) => z.zone_place_id === selectedZone,
      );
      const facilities = (zoneData?.facilities || []).filter(
        (f) => f.district === selectedDistrict,
      );
      setMonitoringData(
        facilities.map((f) => ({
          id: f.place_name,
          placeId: f.place_id,
          hours: f.hours_monitored,
          avgPerDevice: f.avg_monitoring_hours_per_device ?? 0,
          avgPerPregnantWoman: f.avg_monitoring_hours_per_pregnant_woman ?? 0,
        })),
      );
    }
    setIsMonitoringLoading(false);
  }, [monitoringChartZones, selectedFacility, selectedZone, selectedDistrict]);

  useEffect(() => {
    if (globalDateFilter === "custom" && (!startDate || !endDate)) return;
    fetchUltraAdminOverview();
  }, [globalDateFilter, startDate, endDate]);

  useEffect(() => {
    if (globalDateFilter === "custom" && (!startDate || !endDate)) return;
    if (selectedFacility !== "all") {
      fetchDeviceOverview();
    } else {
      fetchUltraAdminPatientChart(globalDateFilter);
      fetchUltraAdminMonitoringChart(globalDateFilter);
    }
  }, [globalDateFilter, startDate, endDate, selectedFacility]);

  const formatDateDisplay = () => {
    if (!startDate || !endDate) return "Select Date";
    if (startDate.isSame(endDate)) {
      return startDate.format("D MMM YYYY");
    }
    const startFormat =
      startDate.year() === endDate.year() ? "D MMM" : "D MMM YYYY";
    return `${startDate.format(startFormat)} - ${endDate.format("D MMM YYYY")}`;
  };

  // Shows the actual active range for whatever is selected in the global
  // date filter — a preset (Today/Yesterday/Last 7 days/etc.) or a custom
  // calendar range — not just when a custom range happens to be picked.
  const formatRange = (start, end) => {
    if (!start || !end) return null;
    if (start.isSame(end, "day")) return start.format("D MMM YYYY");
    const startFormat = start.year() === end.year() ? "D MMM" : "D MMM YYYY";
    return `${start.format(startFormat)} - ${end.format("D MMM YYYY")}`;
  };
  const { start: activeRangeStart, end: activeRangeEnd } =
    getDateRange(globalDateFilter);
  const activeDateRangeDisplay =
    formatRange(activeRangeStart, activeRangeEnd) || "Till Date";

  const open = Boolean(anchorEl);
  const id = open ? "custom-date-popover" : undefined;

  const hierarchyLevel = getCurrentHierarchyLevel();

  let leftDonut = null; // null hides the left donut (single-facility device view)
  let deviceStatusSegments = { active: 0, disconnected: 0 };
  let traceCategoryCounts = {};
  let patientMonitoringFrequency = {};

  if (hierarchyLevel === "zone") {
    leftDonut = {
      title: "Zone status",
      label: "Zone",
      active: summaryData.zone_status?.active || 0,
      inactive: summaryData.zone_status?.inactive || 0,
    };
    deviceStatusSegments = {
      active: summaryData.device_status?.active || 0,
      disconnected: summaryData.device_status?.disconnected || 0,
    };
    traceCategoryCounts = summaryData.trace_category || {};
    patientMonitoringFrequency = summaryData.patient_monitoring_frequency || {};
  } else if (hierarchyLevel === "district") {
    leftDonut = {
      title: "District status",
      label: "District",
      active: districtStatusCounts.active,
      inactive: districtStatusCounts.inactive,
    };
    deviceStatusSegments = {
      active: selectedZoneData?.totals?.device_status?.active || 0,
      disconnected: selectedZoneData?.totals?.device_status?.disconnected || 0,
    };
    traceCategoryCounts = selectedZoneData?.totals?.trace_category || {};
    patientMonitoringFrequency =
      selectedZoneData?.totals?.patient_monitoring_frequency || {};
  } else if (hierarchyLevel === "facility") {
    leftDonut = {
      title: "Facility status",
      label: "Facility",
      active: facilityStatusCounts.active,
      inactive: facilityStatusCounts.inactive,
    };
    deviceStatusSegments = aggregatedDeviceStatus;
    traceCategoryCounts = aggregatedTraceCategory;
    patientMonitoringFrequency = aggregatedPatientMonitoringFrequency;
  } else {
    deviceStatusSegments = {
      active: selectedFacilityData?.device_status?.active || 0,
      disconnected: selectedFacilityData?.device_status?.disconnected || 0,
    };
    traceCategoryCounts = selectedFacilityData?.trace_category || {};
    patientMonitoringFrequency =
      selectedFacilityData?.patient_monitoring_frequency || {};
  }

const totalAlertsGenerated =
    (traceCategoryCounts.suspicious || 0) + (traceCategoryCounts.pathological || 0);

  const alertOverviewCategories = [
    {
      label: "Normal",
      value: traceCategoryCounts.normal || 0,
      color: "#6FCF97",
    },
    {
      label: "Suspicious",
      value: traceCategoryCounts.suspicious || 0,
      color: "#F2C265",
    },
    {
      label: "Pathological",
      value: traceCategoryCounts.pathological || 0,
      color: "#F2765A",
    },
  ];

  const recordingsTiles = RECORDING_BUCKETS.map(({ key, label }) => {
    const bucket = patientMonitoringFrequency[key] || {};
    return {
      label,
      percentage: `${(bucket.percentage || 0).toFixed(2)}%`,
      count: (bucket.count || 0).toLocaleString(),
    };
  });

  return (
    <div
      className={`analytics ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}
    >
      <div className="analytics">
        <div className="analytics-section">
          <div className="analytics-page basic-analytics-wrapper">
            <div className="basic-dashboard-header">
              <div className="basic-dashboard-title">Dashboard</div>

              <div className="header-controls">
                {startDate && endDate && (
                  <button className="clear-date-btn" onClick={handleClearDates}>
                    ✕ Clear Custom Date
                  </button>
                )}

                <button
                  className={`custom-date-selector-btn ${open ? "active" : ""} ${
                    startDate && endDate ? "date-chosen" : ""
                  }`}
                  onClick={handleCalendarOpen}
                  aria-describedby={id}
                >
                  <CalendarMonthIcon sx={{ color: "#6e6e6e", fontSize: 22 }} />
                </button>

                {/* Global Date Filter */}
                <FormControl className="facility-dropdown">
                  <Select
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      transformOrigin: { vertical: "top", horizontal: "right" },
                      PaperProps: { style: { marginTop: 4, maxWidth: 280 } },
                    }}
                    value={globalDateFilter}
                    onChange={(e) => {
                      setGlobalDateFilter(e.target.value);
                      setStartDate(null);
                      setEndDate(null);
                      setIsSelectingStartDate(true);
                    }}
                    className="facility-select"
                    renderValue={(selected) =>
                      selected === "custom"
                        ? formatDateDisplay()
                        : DATE_FILTER_LABELS[selected] || "Till Date"
                    }
                    sx={{
                      minWidth: globalDateFilter === "custom" ? 240 : 170,
                      height: 40,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 32px 8px 12px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="all">Till Date</MenuItem>
                    <MenuItem value="month">Current Month</MenuItem>
                    <MenuItem value="day">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="week">Last 7 days</MenuItem>
                    <MenuItem value="last15days">Last 15 days</MenuItem>
                    <MenuItem value="last30days">Last 30 Days</MenuItem>
                    <MenuItem value="custom" disabled>
                      {formatDateDisplay()}
                    </MenuItem>
                  </Select>
                </FormControl>

                <FormControl className="facility-dropdown">
                  <Select
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      transformOrigin: { vertical: "top", horizontal: "right" },
                      PaperProps: { style: { marginTop: 4, maxWidth: 280 } },
                    }}
                    value={selectedZone}
                    onChange={(e) => handleZoneChange(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === "all") return "All Zones";
                      const zone = zoneOptions.find((z) => z.id === selected);
                      const name = zone?.name || "";
                      return (
                        <span
                          title={name}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            maxWidth: "140px",
                          }}
                        >
                          {name}
                        </span>
                      );
                    }}
                    className="facility-select"
                    sx={{
                      minWidth: 150,
                      height: 40,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 48px 8px 12px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="all">All Zones</MenuItem>
                    {zoneOptions.map((z) => (
                      <MenuItem
                        key={z.id}
                        value={z.id}
                        title={z.name}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                            fontWeight: 500,
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                          },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          maxWidth: "100%",
                        }}
                      >
                        {z.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* District Filter */}
                <FormControl className="facility-dropdown">
                  <Select
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      transformOrigin: { vertical: "top", horizontal: "right" },
                      PaperProps: { style: { marginTop: 4, maxWidth: 280 } },
                    }}
                    value={selectedDistrict}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === "all")
                        return "All Districts";
                      return (
                        <span
                          title={selected}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            maxWidth: "140px",
                          }}
                        >
                          {selected}
                        </span>
                      );
                    }}
                    className="facility-select"
                    sx={{
                      minWidth: 150,
                      height: 40,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 44px 8px 12px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="all">All Districts</MenuItem>
                    {districtOptions.map((d) => (
                      <MenuItem
                        key={d}
                        value={d}
                        title={d}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                            fontWeight: 500,
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                          },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          maxWidth: "100%",
                        }}
                      >
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Facility Filter */}
                <FormControl className="facility-dropdown">
                  <Select
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "right" },
                      transformOrigin: { vertical: "top", horizontal: "right" },
                      PaperProps: { style: { marginTop: 4, maxWidth: 280 } },
                    }}
                    value={selectedFacility}
                    onChange={(e) => handleFacilitySelect(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected === "all")
                        return "All Facilities";
                      const facility = facilityOptionsFromZone.find(
                        (f) => f.id === selected,
                      );
                      const name = facility?.name || "";
                      return (
                        <span
                          title={name}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            maxWidth: "140px",
                          }}
                        >
                          {name}
                        </span>
                      );
                    }}
                    className="facility-select"
                    sx={{
                      minWidth: 190,
                      height: 40,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 44px 8px 12px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <MenuItem value="all">All Facilities</MenuItem>
                    {facilityOptionsFromZone.map((f) => (
                      <MenuItem
                        key={f.id}
                        value={f.id}
                        title={f.name}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                            fontWeight: 500,
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor: "#F6F2FF",
                            color: "rgba(108, 23, 217, 1)",
                          },
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          maxWidth: "100%",
                        }}
                      >
                        {f.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleCalendarClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  className: "custom-calendar-popover",
                }}
              >
                <div className="date-range-picker">
                  {startDate && endDate && (
                    <div className="selected-range-display">
                      Selected : {formatDateDisplay()}
                    </div>
                  )}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      className="custom-mui-calendar"
                      value={isSelectingStartDate ? startDate : endDate}
                      onChange={handleDateSelect}
                      disableFuture
                      slots={{ day: CustomDay }}
                      slotProps={{
                        day: (dayProps) => ({
                          ...dayProps,
                          startDate,
                          endDate,
                        }),
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </Popover>
            </div>

            {/* Top Stats Cards Container */}
            <AnalyticsStatCards
              values={{
                total_hospitals: filteredSummary.total_hospitals,
                total_devices: filteredSummary.total_devices,
                total_alerts_generated: totalAlertsGenerated,
                total_hours_monitored: filteredSummary.total_hours_monitored,
                unique_patients_monitored:
                  filteredSummary.total_unique_patients_monitored,
                total_ctg_sessions: filteredSummary.total_ctg_sessions,
              }}
              isLoading={isSummaryLoading}
            />

            <PatientStats
              data={patientData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isPatientLoading}
              hasError={patientError}
              onBarClick={handleBarClick}
            />

            <AlertOverview
              categories={alertOverviewCategories}
              dateRangeDisplay={activeDateRangeDisplay}
            />

            <MonitoringStats
              data={monitoringData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isMonitoringLoading}
              hasError={monitoringError}
              onBarClick={handleBarClick}
            />

            <UniquePatientMonitored
              tiles={recordingsTiles}
              dateRangeDisplay={activeDateRangeDisplay}
            />

            <CtgSessions
              data={ctgSessionsData}
              dateRangeDisplay={activeDateRangeDisplay}
              isLoading={isPatientLoading}
              hasError={patientError}
              onBarClick={handleBarClick}
            />

            <div className="status-donut-row">
              {leftDonut && (
                <StatusDonutCard
                  title={leftDonut.title}
                  dateRangeDisplay={activeDateRangeDisplay}
                  segments={[
                    {
                      name: `Active ${leftDonut.label}`,
                      legendLabel: "Active",
                      value: leftDonut.active,
                      color: "#9bfdf5",
                    },
                    {
                      name: `Inactive ${leftDonut.label}`,
                      legendLabel: "Inactive",
                      value: leftDonut.inactive,
                      color: "#F792B0",
                    },
                  ]}
                />
              )}

              <StatusDonutCard
                title="Device status"
                dateRangeDisplay={activeDateRangeDisplay}
                segments={[
                  {
                    name: "Active Devices",
                    legendLabel: "Active Devices",
                    value: deviceStatusSegments.active,
                    color: "#C9E7C1",
                  },
                  {
                    name: "Inactive Devices",
                    legendLabel: "Inactive Devices",
                    value: deviceStatusSegments.disconnected,
                    color: "#EA8194",
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UltraAdminAnalytics;
